//dependencies
import React, { useState } from "react";

import { toast } from "@performyard/py-components";

//components
import Button from "../../../../../components/Button";
import EmployeePicker, { SingleEmployee } from "../../../../../components/EmployeePicker";
import FormModal from "../../../../../components/Modal/Templates/FormModal";
import Spacer from "../../../../../components/Spacer";

//hooks
import { useEmployeeCustomFields, useInternalApiUser, useMutateEmployeeActivation } from "../../../../../api/hooks";
import useSearchQueryParams from "../../../../../hooks/useSearchQueryParams";

//config
import Config from "../../../Config";

type ReassignManagerModalProps = {
	isOpen: boolean;
	handleClose: () => void;
	userID: string;
};

const ReassignManagerModal = ({ isOpen, handleClose, userID }: ReassignManagerModalProps) => {
	const [newManager, setNewManager] = useState<SingleEmployee | null>(null);
	const [validationError, setValidationError] = useState<string>("");
	const {
		queryParams: { terminationDate },
	} = useSearchQueryParams({ schema: Config.viewPageSchema });
	const { data: user, isLoading } = useInternalApiUser(userID);
	const { data, isLoading: isFieldsLoading } = useEmployeeCustomFields(userID);

	const { mutate: deactivateUser, isPending } = useMutateEmployeeActivation({
		onSuccess: () => {
			handleClose();
			toast.success("User Deactivated");
			if (newManager?.value) {
				toast.success("Users Reassigned");
			}
		},
	});

	if (isLoading || isFieldsLoading) return null;
	const numberOfDirectReports = user?.directReports?.length || 0;
	const managerField = data?.customFields?.find((field) => field.name === "Manager");

	const handleDeactivateUser = () => {
		deactivateUser({
			employeeId: userID,
			activate: false,
			termination: terminationDate,
			...(newManager?.value && { managerReassignmentId: newManager.value }),
		});
	};

	return (
		<FormModal
			isOpen={isOpen}
			handleClose={handleClose}
			buttons={[
				<Button
					key="reassign-cancel"
					onClick={() => {
						handleDeactivateUser();
					}}
					isRequesting={!newManager?.value && isPending}
				>
					Skip
				</Button>,
				<Button key="reassign-submit" color="red" submit isRequesting={newManager?.value && isPending}>
					Assign New Manager
				</Button>,
			]}
			onSubmit={(event) => {
				event.preventDefault();
				if (!newManager) {
					setValidationError("This is a required field");
					return;
				} else if (!managerField?._id) {
					setValidationError("No manager field found");
				} else {
					setValidationError("");
					handleDeactivateUser();
				}
			}}
			contentClassName="flex flex-col items-center justify-center"
		>
			<div className="max-w-96 text-wrap break-words text-center">
				<span className="text-paragraph-2 font-semi-bold">{user?.name} </span>
				<span className="text-paragraph-3 font-regular">had </span>
				<span className="text-paragraph-4 font-semi-bold">
					{numberOfDirectReports} Direct Report{numberOfDirectReports > 1 ? "s" : ""}{" "}
				</span>
				<span className="text-paragraph-2 font-regular">would you like to assign them a new manager?</span>
			</div>
			<Spacer height={3} />
			<div className="w-full">
				<EmployeePicker
					exclusionList={[userID]}
					label="Select a New Manager"
					errorMessage={validationError}
					value={newManager || ""}
					onChange={(value) => setNewManager(value as SingleEmployee | null)}
				/>
			</div>
		</FormModal>
	);
};

export default ReassignManagerModal;
